/**
 * load-seed.ts — runtime accessors for CFG-129 seed data.
 *
 * The seed files are pure TS modules — no I/O. These accessors give the
 * web/api callers a uniform locale-aware API.
 */

import { VALUES_DE, VALUES_EN, VALUES_POOL_VERSION } from './values-pool-60.js';
import { VIRTUES_POOL, VIRTUES_POOL_VERSION, type VirtueDomain, type VirtueEntry } from './virtues-pool.js';
import { WOL_AXES, WOL_AXES_VERSION, type WolAxis, type WolAxisKey } from './wol-axes.js';

export type SeedLocale = 'de' | 'en';

/** Canonical 60-card values pool, localized. */
export function loadValuesPool(locale: SeedLocale): readonly string[] {
  return locale === 'de' ? VALUES_DE : VALUES_EN;
}

/** Virtues pool — domain-tagged entries with both DE + EN. Caller picks. */
export function loadVirtuesPool(): readonly VirtueEntry[] {
  return VIRTUES_POOL;
}

/** Virtues pool filtered by domain (energy / work / love). */
export function loadVirtuesByDomain(domain: VirtueDomain): readonly VirtueEntry[] {
  return VIRTUES_POOL.filter((virtue) => virtue.domain === domain);
}

/** Wheel of Life axes, localized — `[{key, label}]`. */
export function loadWolAxes(
  locale: SeedLocale,
): readonly { key: WolAxisKey; label: string }[] {
  return WOL_AXES.map((axis) => ({
    key: axis.key,
    label: locale === 'de' ? axis.de : axis.en,
  }));
}

/** All seed-version constants, useful for an `/api/v1/seed/version` future endpoint. */
export const SEED_VERSIONS = {
  values: VALUES_POOL_VERSION,
  virtues: VIRTUES_POOL_VERSION,
  wolAxes: WOL_AXES_VERSION,
} as const;

export type { VirtueDomain, VirtueEntry, WolAxis, WolAxisKey };
